import React, { useState } from 'react';
import { Input } from '@/components/ui/Input';

interface LanguagesSectionProps {
  /** Meta locale IDs (numeric). */
  locales: number[];
  onChange: (locales: number[]) => void;
}

/** Common Meta locale IDs. Full list lives on Meta; this static set
 *  covers the most common English-speaking + EU + APAC markets so the
 *  wizard ships without a `/meta/locales-search` proxy.
 *
 *  IDs sourced from Meta's locale endpoint snapshot (stable enum). */
const COMMON_LOCALES: Array<{ id: number; name: string }> = [
  { id: 6, name: 'English (US)' },
  { id: 24, name: 'English (UK)' },
  { id: 5, name: 'French' },
  { id: 13, name: 'German' },
  { id: 16, name: 'Italian' },
  { id: 9, name: 'Spanish' },
  { id: 19, name: 'Portuguese (Brazil)' },
  { id: 23, name: 'Portuguese (Portugal)' },
  { id: 26, name: 'Dutch' },
  { id: 50, name: 'Japanese' },
  { id: 79, name: 'Korean' },
  { id: 27, name: 'Chinese (Simplified)' },
  { id: 31, name: 'Chinese (Traditional)' },
  { id: 46, name: 'Arabic' },
  { id: 105, name: 'Hindi' },
];

export const LanguagesSection: React.FC<LanguagesSectionProps> = ({
  locales,
  onChange,
}) => {
  const [manual, setManual] = useState('');

  const toggle = (id: number) => {
    if (locales.includes(id)) {
      onChange(locales.filter((x) => x !== id));
    } else {
      onChange([...locales, id]);
    }
  };

  // Strict integer check: parseInt('1.5') returns 1 (silent truncation),
  // which would let a user accidentally add locale #1 when they typed
  // a decimal. Require the trimmed text to match `^\d+$` so decimals
  // and signs are rejected outright.
  const parseStrictPositiveInt = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return n > 0 ? n : null;
  };

  const addManual = () => {
    const id = parseStrictPositiveInt(manual);
    if (id !== null && !locales.includes(id)) {
      onChange([...locales, id]);
      setManual('');
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Optional — restrict delivery to specific languages. Empty = no
        language restriction.
      </p>

      <div className="flex flex-wrap gap-2">
        {locales.map((id) => {
          const known = COMMON_LOCALES.find((l) => l.id === id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              aria-label={`Remove ${known?.name ?? id}`}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {known ? known.name : `Locale #${id}`}{' '}
              <span aria-hidden="true" className="ml-1">×</span>
            </button>
          );
        })}
        {locales.length === 0 && (
          <p className="text-xs text-gray-500">No languages selected.</p>
        )}
      </div>

      <div>
        <p className="text-xs font-medium text-gray-700 mb-1">
          Common languages
        </p>
        <div className="flex flex-wrap gap-1">
          {COMMON_LOCALES.map((l) => {
            const selected = locales.includes(l.id);
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => toggle(l.id)}
                aria-pressed={selected}
                className={`text-xs px-2 py-1 border rounded ${
                  selected
                    ? 'bg-blue-50 border-blue-400 text-blue-900'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {l.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label
          htmlFor="meta-locale-manual"
          className="block text-xs font-medium text-gray-700 mb-1"
        >
          Add by Meta locale ID
        </label>
        <div className="flex gap-2">
          <Input
            id="meta-locale-manual"
            type="number"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="e.g. 6"
            className="w-32"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addManual();
              }
            }}
          />
          <button
            type="button"
            onClick={addManual}
            disabled={parseStrictPositiveInt(manual) === null}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
